import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material/styles";
import { useTranslation } from "hooks/useTranslation";

const spinReverse = keyframes`
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.2; transform: scale(0.85); }
  40% { opacity: 1; transform: scale(1); }
`;

// Full-screen loader shown while the app or a lazy-loaded page is loading.
// Sits above the fixed logo/title overlay in App.js so nothing half-rendered shows through.
export default function PageLoader() {
  const { t } = useTranslation();
  const loadingText = t("common.loading", "Loading");

  return (
    <Box
      role="progressbar"
      aria-label={loadingText}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        bgcolor: "#f8fafc",
      }}
    >
      <Box sx={{ position: "relative", width: 64, height: 64 }}>
        <CircularProgress
          size={64}
          thickness={4}
          sx={{ color: "#1976d2", position: "absolute", inset: 0 }}
        />
        <CircularProgress
          size={44}
          thickness={5}
          sx={{
            color: "#64b5f6",
            position: "absolute",
            top: 10,
            left: 10,
            animation: `${spinReverse} 1.4s linear infinite`,
            "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
        <Typography
          sx={{ fontWeight: 600, color: "#0f172a", letterSpacing: 0.3, fontSize: "0.95rem" }}
        >
          {loadingText}
        </Typography>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              bgcolor: "#1976d2",
              animation: `${dotPulse} 1.2s ease-in-out infinite`,
              animationDelay: `${i * 0.15}s`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
